// src/components/WebAuth/WebLogin.js
import React, { useState, useEffect } from 'react';
import { useWebAuth } from '../../context/WebAuthContext';

// Компонент экрана подтверждения (вынесен наружу)
const VerificationPendingScreen = ({ email, onResend, onComplete }) => {
  const [resendCooldown, setResendCooldown] = useState(0);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResendCooldown(60);
    const result = await onResend();
    if (result.success) {
      setMessage('✅ Письмо отправлено повторно!');
      setTimeout(() => setMessage(''), 3000);
    } else {
      setMessage('❌ Ошибка отправки');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#180749] to-[#2a0f6e] flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 w-full max-w-md">
        <div className="text-center">
          <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Подтвердите email</h2>
          <p className="text-white/70 mb-4">
            Мы отправили письмо с подтверждением на <strong className="text-white">{email}</strong>
          </p>
          <div className="bg-white/10 rounded-lg p-4 mb-6">
            <p className="text-white/60 text-sm">
              📧 Пожалуйста, проверьте ваш почтовый ящик (включая папку "Спам") и перейдите по ссылке в письме.
            </p>
          </div>
          
          {message && (
            <div className={`mb-4 p-3 rounded-lg ${message.includes('✅') ? 'bg-green-500/20 text-green-200' : 'bg-red-500/20 text-red-200'}`}>
              {message}
            </div>
          )}
          
          <button
            onClick={handleResend}
            disabled={resendCooldown > 0}
            className="w-full py-3 rounded-xl border border-white/30 text-white/70 font-semibold hover:bg-white/10 transition disabled:opacity-50 mb-3"
          >
            {resendCooldown > 0 
              ? `Отправить повторно через ${resendCooldown} сек` 
              : 'Отправить письмо повторно'}
          </button>
          
          <button
            onClick={onComplete}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold hover:opacity-90 transition"
          >
            Я подтвердил email, войти
          </button>
        </div>
      </div>
    </div>
  );
};

const WebLogin = ({ onSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLocalLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  
  const { 
    loginWithEmail, 
    registerWithEmail, 
    resendVerificationEmail,
    checkEmailExists: checkEmail,
    pendingVerification,
    clearPendingVerification
  } = useWebAuth();

  // ВСЕ ХУКИ ДОЛЖНЫ БЫТЬ ВЫЗВАНЫ ДО ЛЮБЫХ УСЛОВНЫХ РЕНДЕРОВ
  // Debounced email check
  useEffect(() => {
    if (!email || isLogin) {
      setEmailExists(false);
      return;
    }
    
    const delayDebounceFn = setTimeout(async () => {
      if (email && email.includes('@')) {
        setCheckingEmail(true);
        try {
          const exists = await checkEmail(email);
          setEmailExists(exists);
        } catch (err) {
          console.error("Email check error:", err);
        }
        setCheckingEmail(false);
      }
    }, 500);
    
    return () => clearTimeout(delayDebounceFn);
  }, [email, isLogin, checkEmail]);

  // Таймер повторной отправки
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Автоскрытие сообщений
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLocalLoading(true);
    
    if (isLogin) {
      const result = await loginWithEmail(email, password);
      
      if (result.success && onSuccess) {
        onSuccess();
      } else if (result.error) {
        setError(result.error);
      }
    } else {
      if (!fullName.trim()) {
        setError('Введите полное имя');
        setLocalLoading(false);
        return;
      }
      if (password.length < 6) {
        setError('Пароль должен содержать минимум 6 символов');
        setLocalLoading(false);
        return;
      }
      
      if (emailExists) {
        setError('Пользователь с таким email уже существует');
        setIsLogin(true);
        setLocalLoading(false);
        return;
      }
      
      const result = await registerWithEmail(email, password, fullName, phoneNumber);
      
      if (result.success) {
        setSuccessMessage('✅ Письмо с подтверждением отправлено! Проверьте ваш email.');
        setPassword('');
        setFullName('');
        setPhoneNumber('');
      } else if (result.error) {
        setError(result.error);
      }
    }
    
    setLocalLoading(false);
  };

  // Если есть ожидающее подтверждение - показываем экран (после всех хуков)
  if (pendingVerification) {
    return (
      <VerificationPendingScreen 
        email={pendingVerification.email}
        userId={pendingVerification.userId}
        onResend={resendVerificationEmail}
        onComplete={() => {
          clearPendingVerification();
          setIsLogin(true);
          setEmail(pendingVerification.email);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#180749] to-[#2a0f6e] flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 w-full max-w-md">
        <h2 className="text-2xl font-bold text-white text-center mb-6">
          {isLogin ? 'Вход' : 'Регистрация'}
        </h2>
        
        {error && (
          <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 mb-4">
            <p className="text-red-200 text-sm whitespace-pre-line">{error}</p>
          </div>
        )}
        
        {successMessage && (
          <div className="bg-green-500/20 border border-green-500 rounded-lg p-3 mb-4">
            <p className="text-green-200 text-sm">{successMessage}</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full px-4 py-3 rounded-xl bg-white/20 text-white placeholder-white/50 border ${
                emailExists && !isLogin && email
                  ? 'border-red-500'
                  : 'border-white/30'
              } focus:outline-none focus:border-white`}
              required
              disabled={loading}
            />
            {checkingEmail && (
              <p className="text-white/40 text-xs mt-1">⏳ Проверка email...</p>
            )}
            {emailExists && !isLogin && email && (
              <p className="text-red-300 text-xs mt-1">
                ⚠️ Пользователь с таким email уже существует. Войдите в аккаунт.
              </p>
            )}
          </div>
          
          <div>
            <input
              type="password"
              placeholder="Пароль"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white/20 text-white placeholder-white/50 border border-white/30 focus:outline-none focus:border-white"
              required
              disabled={loading}
            />
          </div>
          
          {!isLogin && (
            <>
              <div>
                <input
                  type="text"
                  placeholder="Полное имя *"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/20 text-white placeholder-white/50 border border-white/30 focus:outline-none focus:border-white"
                  required
                  disabled={loading}
                />
              </div>
              <div>
                <input
                  type="tel"
                  placeholder="Телефон (необязательно)"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/20 text-white placeholder-white/50 border border-white/30 focus:outline-none focus:border-white"
                  disabled={loading}
                />
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-white/60 text-xs">
                  📧 После регистрации на ваш email будет отправлено письмо с подтверждением.
                </p>
              </div>
            </>
          )}
          
          <button
            type="submit"
            disabled={loading || (emailExists && !isLogin)}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold hover:opacity-90 transition disabled:opacity-50"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin">⏳</span>
                {isLogin ? 'Вход...' : 'Регистрация...'}
              </span>
            ) : (isLogin ? 'Войти' : 'Зарегистрироваться')}
          </button>
        </form>
        
        {/* 🔹 ССЫЛКА "ЗАБЫЛИ ПАРОЛЬ?" - ДОБАВЛЕНА ТОЛЬКО ДЛЯ РЕЖИМА ВХОДА 🔹 */}
        {isLogin && (
          <button
            onClick={() => {
              window.location.href = '/reset-password';
            }}
            className="w-full text-center text-white/50 hover:text-white mt-2 text-sm transition"
          >
            Забыли пароль?
          </button>
        )}
        
        <button
          onClick={() => {
            setIsLogin(!isLogin);
            setError('');
            setSuccessMessage('');
            setEmailExists(false);
          }}
          className="w-full text-center text-white/70 hover:text-white mt-4 text-sm transition"
        >
          {isLogin ? 'Нет аккаунта? Зарегистрироваться' : 'Уже есть аккаунт? Войти'}
        </button>
      </div>
    </div>
  );
};

export default WebLogin;